import useIsMobile from '../hooks/useIsMobile';
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../api/axios';
import PageHeader from '../components/ui/PageHeader';

const roleConfig = {
  notary:     { label: 'Notario',    bg: '#EEF0FB', color: '#4F63D2' },
  supervisor: { label: 'Supervisor', bg: '#DBEAFE', color: '#1D4ED8' },
  staff:      { label: 'Staff',      bg: '#F3F4F6', color: '#374151' },
};

const inputSt = { width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };
const focusSt = e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
const blurSt  = (e, err) => { e.target.style.borderColor = err ? '#EF4444' : '#E5E7EB'; e.target.style.boxShadow = 'none'; };

const Field = ({ label, required, error, hint, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
      {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </label>
    {children}
    {hint && !error && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9CA3AF' }}>{hint}</p>}
    {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#EF4444' }}>{error}</p>}
  </div>
);

const Profile = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: '', password: '', password_confirmation: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const rc = roleConfig[user?.role] || roleConfig.staff;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirmation) {
      setErrors({ password_confirmation: ['Las contraseñas no coinciden'] });
      return;
    }
    setLoading(true);
    try {
      await api.put('/profile/password', form);
      toast.success('Contraseña actualizada correctamente');
      setForm({ current_password: '', password: '', password_confirmation: '' });
    } catch (error) {
      if (error.response?.status === 422) setErrors(error.response.data.errors);
      else if (error.response?.status === 403) setErrors({ current_password: ['La contraseña actual es incorrecta'] });
      else toast.error('Error al actualizar contraseña');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title="Mi Perfil" subtitle="Información de tu cuenta y seguridad" />

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px 1fr', gap: 16, alignItems: 'start' }}>
        {/* Tarjeta de perfil */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '28px 20px', textAlign: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 28, fontWeight: 800, color: '#4F63D2' }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <h5 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#111827' }}>{user?.name}</h5>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#9CA3AF' }}>{user?.email}</p>
          <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20, backgroundColor: rc.bg, color: rc.color }}>
            {rc.label}
          </span>

          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #F3F4F6', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: 'Rol', value: rc.label },
              { label: 'Correo', value: user?.email },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#9CA3AF' }}>{label}</span>
                <span style={{ color: '#374151', fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Cambiar contraseña */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 14, color: '#111827' }}>
            Cambiar Contraseña
          </div>
          <div style={{ padding: 20 }}>
            <form onSubmit={handleSubmit}>
              <Field label="Contraseña actual" required error={errors.current_password?.[0]}>
                <input type="password" name="current_password" value={form.current_password} onChange={handleChange} required
                  style={{ ...inputSt, borderColor: errors.current_password ? '#EF4444' : '#E5E7EB' }}
                  onFocus={focusSt} onBlur={e => blurSt(e, !!errors.current_password)} />
              </Field>
              <Field label="Nueva contraseña" required error={errors.password?.[0]} hint="Mínimo 8 caracteres">
                <input type="password" name="password" value={form.password} onChange={handleChange} required minLength={8}
                  style={{ ...inputSt, borderColor: errors.password ? '#EF4444' : '#E5E7EB' }}
                  onFocus={focusSt} onBlur={e => blurSt(e, !!errors.password)} />
              </Field>
              <Field label="Confirmar nueva contraseña" required error={errors.password_confirmation?.[0]}>
                <input type="password" name="password_confirmation" value={form.password_confirmation} onChange={handleChange} required
                  style={{ ...inputSt, borderColor: errors.password_confirmation ? '#EF4444' : '#E5E7EB' }}
                  onFocus={focusSt} onBlur={e => blurSt(e, !!errors.password_confirmation)} />
              </Field>
              <button type="submit" disabled={loading}
                style={{ padding: '9px 22px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Actualizando...' : 'Actualizar contraseña'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
